import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemText,
  Typography,
  ListItemButton,
  Grid2,
} from '@mui/material';
import React, { useState } from 'react';
import { stripHtmlTags } from '../../utils/stripHtmlTags';
import {
  FbeForm,
  FbeFormFeature,
  FbeQuestion,
  FbeQuestionFeature,
  FbeQuestionType,
  FbeSection,
  FbeSectionDisplayType,
  FbeSectionFeature,
} from '../../services/fbeTypes';
import { CopyAll, Delete } from '@mui/icons-material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import ConfirmDialog from '../ConfirmDialog';
import { deepEqual, isFormValid, isQuestionValid, isSectionValid } from '../../services/service';

const Sidebar = ({
  readOnly,
  selectedIndex,
  selectedSectionIndex,
  selectedQuestionIndex,
  handleListItemClick,
  handleSectionListItemClick,
  handleQuestionListItemClick,
  formName,
  sections,
  originalSections,
  form,
  originalForm,
  isLocalAdmin,
  onAddNewSection,
  onAddNewQuestion,
  onDeleteSection,
  onDeleteQuestion,
  onReorderSections,
  onReorderQuestions,
  onDuplicateSection,
  onDuplicateQuestion,
  language
}: {
  readOnly: boolean;
  isLocalAdmin: boolean;
  selectedIndex: number | null;
  selectedSectionIndex: number | null;
  selectedQuestionIndex: number | null;
  handleListItemClick: (index: number) => void;
  handleSectionListItemClick: (index: number) => void;
  handleQuestionListItemClick: (
    sectionIndex: number,
    questionIndex: number
  ) => void;
  formName?: string;
  sections: FbeSection[];
  originalSections: FbeSection[];
  form: FbeForm;
  originalForm: FbeForm;
  onAddNewSection?: (newSection: FbeSection) => void;
  onAddNewQuestion?: (sectionIndex: number, newQuestion: FbeQuestion) => void;
  onDeleteSection?: (index: number) => void;
  onDeleteQuestion?: (sectionIndex: number, questionIndex: number) => void;
  onReorderSections?: (newOrder: FbeSection[], index: number) => void;
  onReorderQuestions?: (newSections: FbeSection[], sectionIndex: number, questionIndex: number) => void;
  onDuplicateSection?: (index: number) => void;
  onDuplicateQuestion?: (sectionIndex: number, questionIndex: number) => void;
  language: string;
}): JSX.Element => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogSectionIndex, setDialogSectionIndex] = useState<number>(-1);
  const [dialogQuestionIndex, setDialogQuestionIndex] = useState<number>(-1);
  const [sectionDialog, setSectionDialog] = useState<boolean>(false);

  const handleAddSection = () => {
    const newSection: FbeSection = {
      active: true,
      order: sections.length,
      name: [{ language:`${language}`, text: 'New section' }],
      questions: [],
      tasks: [],
      displayType: FbeSectionDisplayType.LIST
    };
    if (onAddNewSection) onAddNewSection(newSection); // Call parent function to add section
  };

  const handleAddQuestion = (sectionIndex: number) => {
    const newQuestion: FbeQuestion = {
      question: [{ language: `${language}`, text: 'New Question' }],
      order: sections[sectionIndex]?.questions.length || 0, // Maintain order
      questionType: '' as unknown as FbeQuestionType,
      questionId: `question_group_${form.pk}_${form.sk}_${sections[sectionIndex || 0].sk || sections[sectionIndex || 0].order}_${sections[sectionIndex]?.questions.length || 0}`
    };

    if (onAddNewQuestion) onAddNewQuestion(sectionIndex, newQuestion);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, type } = result;

    if (type === 'section') {
      // Reorder sections
      const reorderedSections = [...JSON.parse(JSON.stringify(sections)) as FbeSection[]];
      const [movedSection] = reorderedSections.splice(source.index, 1);
      reorderedSections.splice(destination.index, 0, movedSection);

      // Update order field in sections
      reorderedSections.forEach((section, index) => {
        section.order = index;
        section.internalCode = `${section.pk}_${index}`
      });

      const selected = reorderedSections.find(reorderedSection => reorderedSection.name[0].text === movedSection.name[0].text);

      onReorderSections?.(reorderedSections, selected?.order || 0);
    } else if (type.startsWith('question')) {
      const sourceSectionIndex = parseInt(source.droppableId.split('-')[1]);
      const destSectionIndex = parseInt(destination.droppableId.split('-')[1]);
      const updatedSections = [...JSON.parse(JSON.stringify(sections)) as FbeSection[]];

      const [movedQuestion] = updatedSections[sourceSectionIndex].questions.splice(source.index, 1);
      updatedSections[destSectionIndex].questions.splice(destination.index, 0, movedQuestion);
      updatedSections[destSectionIndex].questions = updatedSections[destSectionIndex].questions.map( (question,index) => {
        return {
          ...question,
          order: index,
        }
      });

      // Update question orders in both affected sections
      [sourceSectionIndex, destSectionIndex].forEach((sectionIdx) => {
        updatedSections[sectionIdx].questions.forEach((q, i) => {
          q.order = i;
        });
      });

      const selected = updatedSections[destSectionIndex].questions.find(question => question.question[0].text === movedQuestion.question[0].text);

      onReorderQuestions?.(updatedSections, destSectionIndex, selected?.order || 0);
      sections = JSON.parse(JSON.stringify(updatedSections)) as FbeSection[];
    }
  };

  const SECTION_DIALOG_TITLE = 'Delete Section';
  const SECTION_DIALOG_CONTENT = 'Are you sure you want to delete this section?';
  const QUESTION_DIALOG_TITLE = 'Delete Question';
  const QUESTION_DIALOG_CONTENT = 'Are you sure you want to delete this question?';

  const dialogTitle = sectionDialog ? SECTION_DIALOG_TITLE : QUESTION_DIALOG_TITLE;
  const dialogContent = sectionDialog ? SECTION_DIALOG_CONTENT : QUESTION_DIALOG_CONTENT;

  const dialogOpen = (isSection: boolean, sectionIndex: number, questionIndex: number) => {
    setSectionDialog(isSection);
    setDialogSectionIndex(sectionIndex);
    setDialogQuestionIndex(questionIndex);
    setOpenDialog(true);
}

  const dialogConfirm = () => {
    setOpenDialog(false);
    setDialogSectionIndex(-1);
    setDialogQuestionIndex(-1);
    if(dialogQuestionIndex === -1 && dialogSectionIndex >= 0){
      onDeleteSection &&
      onDeleteSection(dialogSectionIndex);
    } else if(dialogSectionIndex >= 0 && dialogQuestionIndex >= 0){
      onDeleteQuestion &&
      onDeleteQuestion(dialogSectionIndex, dialogQuestionIndex);
    }
  }

  const dialogCancel = () => {
    setOpenDialog(false);
    setDialogSectionIndex(-1);
    setDialogQuestionIndex(-1);
  }

  const isFormChanged = (): boolean => {

//  console.error(`originalForm ${JSON.stringify(originalForm)}`)
//  console.error(`form ${JSON.stringify(form)}`)

  if(!originalForm || !form) { return true; }

    return !deepEqual(originalForm, form);
  }

  const isSectionChanged = (sectionIndex: number): boolean => {
    const originalSection = sectionIndex >= originalSections.length ? null : originalSections[sectionIndex];
    const currentSection = sectionIndex >= sections.length ? null : sections[sectionIndex];

    // console.error(`${sectionIndex} originalSection ${JSON.stringify(originalSection)}`)
    // console.error(`${sectionIndex} currentSection ${JSON.stringify(currentSection)}`)

    if(!originalSection || !currentSection) { return true; }
    return !deepEqual(originalSection, currentSection);
  }

  const isQuestionChanged = (sectionIndex: number, questionIndex: number): boolean => {
    const originalSection = sectionIndex >= originalSections.length ? null : originalSections[sectionIndex];
    const currentSection = sectionIndex >= sections.length ? null : sections[sectionIndex];

    if(!originalSection || !currentSection) { return true; }

    const originalQuestion = questionIndex >= originalSection.questions.length ? null : originalSection.questions[questionIndex];
    const currentQuestion = questionIndex >= currentSection.questions.length ? null : currentSection.questions[questionIndex];

    if(!originalQuestion || !currentQuestion) { return true; }

//    console.error(`originalQuestion ${JSON.stringify(originalQuestion)}`)
//    console.error(`currentQuestion ${JSON.stringify(currentQuestion)}`)

    return !deepEqual(originalQuestion, currentQuestion);
  }

//  console.error(`sections ${JSON.stringify(sections)} `);
//  console.error(`originalSections ${JSON.stringify(originalSections)} `);


  return (
    <Grid2 size={{ xs: 12, sm: 4, md: 3 }}>
      {/* Form Name */}
      <Box
        borderRight={{ sm: '1px solid #e0e0e0' }}
        p={2}
        bgcolor="#f9f9f9"
        height="10vh"
        display="flex"
        flexDirection="column"
      >
        <List
          component="nav"
        >
          <ListItemButton
            selected={selectedIndex === 0}
            onClick={() => handleListItemClick(0)}
            sx={{
              ...(isFormChanged() && isFormValid(form,[]) && {
                backgroundColor: '#F9E20D',
                '&:hover': {
                  backgroundColor: '#F9E20D',
                },
                '&.Mui-selected': {
                  backgroundColor: '#F9E20D',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: '#F9E20D',
                },
                '&:active': {
                  backgroundColor: '#F9E20D',
                },
                '&:focus': {
                  backgroundColor: '#F9E20D',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: '#F9E20D',
                },
                }),
                ...(!isFormValid(form,[]) && {
                  backgroundColor: '#FF2A31',
                  '&:hover': {
                    backgroundColor: '#FF2A31',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#FF2A31',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: '#FF2A31',
                  },
                  '&:active': {
                    backgroundColor: '#FF2A31',
                  },
                  '&:focus': {
                    backgroundColor: '#FF2A31',
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: '#FF2A31',
                  },
                  }),
              }}
          >
            <ListItemText
            primary={
                <Typography
                  sx={{
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'break-spaces',
                    display: 'block',
                    overflowY: 'hidden'
                  }}
                  style={{ fontWeight: 'bold'
                  }}
                >
                  {formName ? formName : 'New Form'}
                </Typography>
              }
            />
          </ListItemButton>
        </List>
      </Box>

      <Box
        borderRight={{ sm: '1px solid #e0e0e0' }}
        p={2}
        bgcolor="#f9f9f9"
        height="60vh"
        display="flex"
        flexDirection="column"
      >
        <List
          component="nav"
          sx={{ overflowY: 'auto', maxHeight: 'calc(60vh - 50px)'
          }}
        >
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="sections" type="section">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {sections.map((section, sectionIndex) => (
                    <Draggable
                      key={`section-${sectionIndex}`}
                      draggableId={`section-${sectionIndex}`}
                      index={sectionIndex}
                      isDragDisabled={(isLocalAdmin && originalForm.features.includes(FbeFormFeature.NO_LOCAL_SECTIONS)) || readOnly}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <ListItemButton
                            {...provided.dragHandleProps}
                            selected={selectedSectionIndex === sectionIndex}
                            onClick={() =>
                              handleSectionListItemClick(sectionIndex)
                            }
                            sx={{
                              ...(isSectionChanged(sectionIndex) && isSectionValid(section,[]) && {
                                backgroundColor: '#F9E20D',
                                '&:hover': {
                                  backgroundColor: '#F9E20D',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F9E20D',
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: '#F9E20D',
                                },
                                '&:active': {
                                  backgroundColor: '#F9E20D',
                                },
                                '&:focus': {
                                  backgroundColor: '#F9E20D',
                                },
                                '&.Mui-focusVisible': {
                                  backgroundColor: '#F9E20D',
                                },
                                }),
                                ...(!isSectionValid(section,[]) && {
                                  backgroundColor: '#FF2A31',
                                  '&:hover': {
                                    backgroundColor: '#FF2A31',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: '#FF2A31',
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: '#FF2A31',
                                  },
                                  '&:active': {
                                    backgroundColor: '#FF2A31',
                                  },
                                  '&:focus': {
                                    backgroundColor: '#FF2A31',
                                  },
                                  '&.Mui-focusVisible': {
                                    backgroundColor: '#FF2A31',
                                  },
                                  }),
                                }}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  noWrap
                                  sx={{
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'break-spaces',
                                    display: 'block',
                                  }}
                                >
                                  {section.groupId && !section.groupHeader
                                    ? `${sections.find(s => s.groupHeader && s.groupId === section.groupId)?.name?.[0]?.text || ''} > ${section.name?.[0]?.text || 'New section'}`
                                    : section.name?.[0]?.text || 'New section'}
                                </Typography>
                              }
                            />
                            <IconButton
                              edge="end"
                              aria-label="duplicate"
                              disabled={ (isLocalAdmin && originalForm.features.includes(FbeFormFeature.NO_LOCAL_SECTIONS)) ||
                                (isLocalAdmin && (originalSections && originalSections[sectionIndex] && (originalSections[sectionIndex].features || []).includes(FbeSectionFeature.NO_LOCAL_QUESTION))) ||
                                readOnly
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if(onDuplicateSection){onDuplicateSection(sectionIndex)};
                              }}
                            >
                              <CopyAll />
                            </IconButton>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              disabled={(isLocalAdmin && originalForm.features.includes(FbeFormFeature.NO_LOCAL_SECTIONS) )||
                                (isLocalAdmin && (originalSections && originalSections[sectionIndex] && (originalSections[sectionIndex].features || []).includes(FbeSectionFeature.NO_LOCAL_QUESTION))) ||
                                readOnly
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                dialogOpen(true, sectionIndex, -1);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </ListItemButton>

                          {/* QUESTIONS DROPPABLE AREA */}
                          <Droppable
                            droppableId={`questions-${sectionIndex}`}
                            type={'question'}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{ paddingLeft: 32 }}
                              >
                                {section.questions.map(
                                  (question, questionIndex) => (
                                    <Draggable
                                      key={`question-${sectionIndex}-${questionIndex}`}
                                      draggableId={`question-${sectionIndex}-${questionIndex}`}
                                      index={questionIndex}
                                      isDragDisabled={(isLocalAdmin && (
                                        originalForm.features.includes(FbeFormFeature.NO_LOCAL_SECTIONS))) ||
                                        (isLocalAdmin && (originalSections && originalSections[sectionIndex] && (originalSections[sectionIndex].features || []).includes(FbeSectionFeature.NO_LOCAL_QUESTION)) )||
                                        readOnly}

                                    >
                                      {(provided) => (
                                        <ListItemButton
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          selected={
                                            selectedSectionIndex ===
                                              sectionIndex &&
                                            selectedQuestionIndex ===
                                              questionIndex
                                          }
                                          onClick={() =>
                                            handleQuestionListItemClick(
                                              sectionIndex,
                                              questionIndex
                                            )
                                          }
                                          sx={{
                                            margin: '4px',
                                            ...(isQuestionChanged(sectionIndex, questionIndex) && isQuestionValid(question,[]) && {
                                              backgroundColor: '#F9E20D',
                                              '&:hover': {
                                                backgroundColor: '#F9E20D',
                                              },
                                              '&.Mui-selected': {
                                                backgroundColor: '#F9E20D',
                                              },
                                              '&.Mui-selected:hover': {
                                                backgroundColor: '#F9E20D',
                                              },
                                              '&:active': {
                                                backgroundColor: '#F9E20D',
                                              },
                                              '&:focus': {
                                                backgroundColor: '#F9E20D',
                                              },
                                              '&.Mui-focusVisible': {
                                                backgroundColor: '#F9E20D',
                                              },
                                              }),
                                              ...(!isQuestionValid(question,[]) && {
                                                backgroundColor: '#FF2A31',
                                                '&:hover': {
                                                  backgroundColor: '#FF2A31',
                                                },
                                                '&.Mui-selected': {
                                                  backgroundColor: '#FF2A31',
                                                },
                                                '&.Mui-selected:hover': {
                                                  backgroundColor: '#FF2A31',
                                                },
                                                '&:active': {
                                                  backgroundColor: '#FF2A31',
                                                },
                                                '&:focus': {
                                                  backgroundColor: '#FF2A31',
                                                },
                                                '&.Mui-focusVisible': {
                                                  backgroundColor: '#FF2A31',
                                                },
                                                }),

                                          }}                                        >
                                          <ListItemText
                                            primary={
                                              <Typography
                                                noWrap
                                                sx={{
                                                  maxWidth: '150px',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                  display: 'block',
                                                }}
                                              >
                                                {stripHtmlTags(question.question?.[0]?.text || 'New Question')}
                                              </Typography>
                                            }
                                          />
                                          <IconButton
                                            edge="end"
                                            aria-label="duplicate"
                                            disabled={ (isLocalAdmin &&
                                              (originalSections[sectionIndex]?.features ?? []).includes(FbeSectionFeature.NO_LOCAL_QUESTION)) ||
                                              (isLocalAdmin && (question?.features ?? []).includes(FbeQuestionFeature.EXTEND_STATIC_QUESTION)) ||
                                              readOnly
                                            }
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if(onDuplicateQuestion){onDuplicateQuestion(sectionIndex, questionIndex)};
                                            }}
                                          >
                                            <CopyAll />
                                          </IconButton>
                                          <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            disabled={(isLocalAdmin &&
                                              (originalSections[sectionIndex]?.features ?? []).includes(FbeSectionFeature.NO_LOCAL_QUESTION)) ||
                                              (isLocalAdmin && (question?.features ?? []).includes(FbeQuestionFeature.EXTEND_STATIC_QUESTION))||
                                              readOnly
                                            }

                                            onClick={(e) => {
                                              e.stopPropagation();
                                              dialogOpen(false, sectionIndex, questionIndex);
                                            }}
                                          >
                                            <Delete />
                                          </IconButton>
                                        </ListItemButton>
                                      )}
                                    </Draggable>
                                  )
                                )}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                          <ListItemButton
                            onClick={() => handleAddQuestion(sectionIndex)}
                            disabled={readOnly || (isLocalAdmin && originalSections && originalSections[sectionIndex] && (isLocalAdmin && originalSections[sectionIndex].features || []).includes(FbeSectionFeature.NO_LOCAL_QUESTION))}
                          >
                            <ListItemText primary="+ Add Question" />
                          </ListItemButton>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

        </List>
        <ConfirmDialog
          key={openDialog ? 'open' : 'closed'}
          open={openDialog}
          title={dialogTitle}
          content={dialogContent}
          onConfirm={() => dialogConfirm()}
          onCancel={() => dialogCancel()}
        />

      </Box>

      <Box borderRight={{ sm: '1px solid #e0e0e0' }}
        p={2}
        bgcolor="#f9f9f9"
        height="10vh"
        display="flex"
        flexDirection="column"
      >
        <List
          component="nav"
        >
        <Divider />
          <ListItemButton
            selected={false}
            onClick={handleAddSection}
            disabled={readOnly ||
              !form.pk ||
              !form.sk ||
              (isLocalAdmin && originalForm.features.includes(FbeFormFeature.NO_LOCAL_SECTIONS))}
          >
            <ListItemText primary="+ Add Section" />
          </ListItemButton>
          </List>
      </Box>
    </Grid2>
  );
};

export default Sidebar;
